import React, { useEffect, useState } from 'react';
import API from '../services/api';

function AdminMessagesPage() {
  // -- FORM FIELDS for creating a new message
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [targetTherapistId, setTargetTherapistId] = useState('');

  // Additional fields for the enhanced message system
  // priority: "1" (Normal), "2" (Important), "3" (Critical)
  const [priority, setPriority] = useState('1');
  // whether a therapist must acknowledge the message
  const [requiresAck, setRequiresAck] = useState(false);
  // schedule messages for the future (ISO string returned by datetime-local input)
  const [scheduledAt, setScheduledAt] = useState('');
  // date/time after which the message should expire
  const [expiresAt, setExpiresAt] = useState('');
  // date/time until which this message is pinned at the top
  const [pinnedUntil, setPinnedUntil] = useState('');

  // -- MESSAGES state for listing
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  // -- THERAPISTS list for the dropdown
  const [therapists, setTherapists] = useState([]);

  // -- FILTERS & PAGINATION
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);  // each page = 10 messages

  // ----------------------------------------------------------------
  // A) FETCH THERAPISTS once
  // ----------------------------------------------------------------
  const fetchTherapists = async () => {
    try {
      const res = await API.get('/admin/users');
      const therapistList = (res.data || []).filter(u => u.role === 'THERAPIST');
      setTherapists(therapistList);
    } catch (error) {
      console.error('Error fetching therapists:', error);
      alert('Error fetching therapists');
    }
  };

  // ----------------------------------------------------------------
  // B) FETCH MESSAGES with pagination, date filters, search
  // ----------------------------------------------------------------
  const fetchMessages = async (reset = false) => {
    try {
      // page = how many times we've loaded older? so offset = page * limit(10)
      const limit = 10;
      const offset = reset ? 0 : page * limit;

      // build query
      let url = `/admin/messages?limit=${limit}&offset=${offset}`;

      if (startDate) url += `&start=${startDate}`;
      if (endDate)   url += `&end=${endDate}`;
      if (searchTerm) {
        // note: encode
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }

      const res = await API.get(url);
      const newData = res.data || [];

      if (reset) {
        setMessages(newData);
      } else {
        // append
        setMessages((prev) => [...prev, ...newData]);
      }

      // if exactly 10 came back, we assume there's possibly more
      setHasMore(newData.length === limit);
    } catch (err) {
      console.error('Error fetching messages:', err);
      alert('Error fetching messages');
    }
  };

  // ----------------------------------------------------------------
  // C) CREATE NEW MESSAGE
  // ----------------------------------------------------------------
  const createMessage = async (e) => {
    e.preventDefault();
    try {
      let tid = targetTherapistId || null;

      // Build the payload including the new message metadata fields.  Convert
      // the priority string into a number and pass optional date/time fields
      // only if they are set.  The API will use sensible defaults if these
      // are omitted.
      await API.post('/admin/messages', {
        title,
        content,
        imageUrl,
        targetTherapistId: tid,
        priority: parseInt(priority, 10),
        requiresAck: requiresAck,
        scheduledAt: scheduledAt || null,
        expiresAt: expiresAt || null,
        pinnedUntil: pinnedUntil || null
      });

      alert('Message created!');
      // Clear form
      setTitle('');
      setContent('');
      setImageUrl('');
      setTargetTherapistId('');
      setPriority('1');
      setRequiresAck(false);
      setScheduledAt('');
      setExpiresAt('');
      setPinnedUntil('');

      // Refresh messages from scratch
      setPage(0);
      fetchMessages(true);
    } catch (err) {
      console.error('Error creating message:', err);
      alert('Error creating message');
    }
  };

  // ----------------------------------------------------------------
  // D) LOAD OLDER => increment page & fetch
  // ----------------------------------------------------------------
  const handleLoadOlder = () => {
    setPage((p) => p + 1);
  };

  // ----------------------------------------------------------------
  // E) SEARCH / FILTER => reset page=0, reload
  // ----------------------------------------------------------------
  const handleSearch = () => {
    setPage(0);
    fetchMessages(true);
  };

  // ----------------------------------------------------------------
  // F) useEffect => on mount, fetch initial data
  // ----------------------------------------------------------------
  useEffect(() => {
    fetchTherapists();
    // load the first batch of messages
    fetchMessages(true);
    // eslint-disable-next-line
  }, []);

  // Also watch for page changes to load older, *unless* page=0 is triggered by reset
  useEffect(() => {
    if (page > 0) {
      fetchMessages(false);
    }
    // eslint-disable-next-line
  }, [page]);

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <div>
      <h2>Admin Messages</h2>

      {/* CREATE NEW MESSAGE FORM */}
      <form onSubmit={createMessage} style={{ marginBottom: '2rem' }}>
        <div>
          <label>Title:</label><br />
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            style={{ width: '300px' }}
          />
        </div>
        <div>
          <label>Content:</label><br />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            rows={4}
            cols={50}
          />
        </div>
        <div>
          <label>Image URL (optional):</label><br />
          <input
            type="text"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>
        <div>
          <label>Target Therapist (optional):</label><br />
          <select
            value={targetTherapistId}
            onChange={(e) => setTargetTherapistId(e.target.value)}
          >
            <option value="">All Therapists</option>
            {therapists.map(t => (
              <option key={t.id} value={t.id}>
                {t.first_name} {t.last_name}
              </option>
            ))}
          </select>
        </div>

        {/* New fields for advanced message options */}
        <div>
          <label>Priority:</label><br />
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          >
            <option value="1">Normal</option>
            <option value="2">Important</option>
            <option value="3">Critical</option>
          </select>
        </div>
        <div>
          <label>
            <input
              type="checkbox"
              checked={requiresAck}
              onChange={(e) => setRequiresAck(e.target.checked)}
            />
            {' '}Requires acknowledgement
          </label>
        </div>
        <div>
          <label>Scheduled At (optional):</label><br />
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>
        <div>
          <label>Expires At (optional):</label><br />
          <input
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
        <div>
          <label>Pinned Until (optional):</label><br />
          <input
            type="datetime-local"
            value={pinnedUntil}
            onChange={(e) => setPinnedUntil(e.target.value)}
          />
        </div>

        <button type="submit" style={{ marginTop: '1rem' }}>Post Message</button>
      </form>

      {/* FILTERS FOR MESSAGES */}
      <div style={{ marginBottom: '2rem' }}>
        <h3>Filter / Search Messages</h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <label>Start Date:</label><br />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label>End Date:</label><br />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label>Search:</label><br />
            <input
              type="text"
              placeholder="Title, Content, or Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '200px' }}
            />
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <button onClick={handleSearch}>Search / Reset</button>
          </div>
        </div>
      </div>

      {/* MESSAGES LIST */}
      <h3>Displayed Messages</h3>
      {messages.length === 0 && <p>No messages found.</p>}

      {messages.map(msg => (
        <div
          key={msg.id}
          style={{
            border: '1px solid #ccc',
            padding: '1rem',
            marginBottom: '1rem'
          }}
        >
          <h4>{msg.title}</h4>
          {/* Render with HTML support */}
          <p dangerouslySetInnerHTML={{ __html: msg.content }} />

          {msg.image_url && (
            <img
              src={msg.image_url}
              alt="Message"
              style={{ maxWidth: '200px', display: 'block', marginTop: '0.5rem' }}
            />
          )}
          <small>
            By: {msg.creator_first_name || 'Unknown'} {msg.creator_last_name || ''}
            {' on '}
            {new Date(msg.created_at).toLocaleString()}
          </small>

          {msg.target_therapist_id ? (
            <p style={{ fontStyle: 'italic', color: '#777' }}>
              Target: Therapist ID {msg.target_therapist_id}
            </p>
          ) : (
            <p style={{ fontStyle: 'italic', color: '#777' }}>
              Target: All Therapists
            </p>
          )}

          {msg.admin_only && (
            <p style={{ fontStyle: 'italic', color: 'red' }}>
              (Admin‐Only Message)
            </p>
          )}
        </div>
      ))}

      {/* "LOAD OLDER" button */}
      {hasMore && (
        <button onClick={handleLoadOlder}>
          Load Older Messages
        </button>
      )}
    </div>
  );
}

export default AdminMessagesPage;
